<template>
    <div>
        <Card>
            <template #title>
                Contratos
            </template>
            <template #content>

                <Toolbar class="mb-2 bg-gray-100">
                    <template #start>
                        <!-- `flex-wrap`: com o alternador lista/kanban ao lado da
                             Exportação, o grupo mede 332px e não cabe nos 304px
                             da toolbar em 375px de viewport — sem quebrar, a
                             Exportação ficava cortada na borda. -->
                        <div class="flex flex-wrap align-items-center gap-2">
                            <!-- Sem v-model de propósito: com v-model e listener
                                 juntos a ordem dos handlers decide se `modo` já
                                 mudou quando a preferência é salva. Aqui o
                                 handler é o único dono da troca. -->
                            <SelectButton
                                :modelValue="modo"
                                :options="modoOpcoes"
                                optionLabel="label"
                                optionValue="value"
                                :allowEmpty="false"
                                size="small"
                                aria-label="Modo de visualização dos contratos"
                                @update:modelValue="mudarModo"
                            >
                                <template #option="{ option }">
                                    <i :class="option.icon" aria-hidden="true"></i>
                                    <span class="ml-2">{{ option.label }}</span>
                                </template>
                            </SelectButton>
                            <Button icon="pi pi-file-excel" label="Exportação" severity="success" size="small"
                                @click="exportarData" />
                        </div>
                    </template>
                    <template #end>
                        <!-- `flex-wrap` aqui é anterior ao Kanban: os três botões
                             medem 276px e já não cabiam nos 249px da toolbar em
                             320px de viewport. Mesma correção do grupo da
                             esquerda, para a toolbar não cortar botão nenhum. -->
                        <div class="flex flex-wrap items-center gap-1">
                            <Button v-if="permissoes.CONTRATO_INCLUIR" icon="pi pi-cloud-upload" label="Upload" severity="info" size="small"
                                @click="openUploadForm" />
                            <Button v-if="permissoes.CONTRATO_INCLUIR" icon="pi pi-plus" label="Incluir" severity="info" size="small"
                                @click="openForm({})" />
                            <!-- Os filtros da busca valem só para a lista: o endpoint do
                                 Kanban não recebe filtro. Botão escondido no Kanban em vez
                                 de abrir um formulário que não muda nada na tela. -->
                            <Button v-if="modo === 'lista'" icon="pi pi-search" label="Buscar" severity="success" size="small"
                                @click="toggleBusca" />
                            <!-- Colunas visíveis do Kanban. Sem v-model, mesma razão do
                                 SelectButton do modo: o handler é o único dono da troca —
                                 e é ele que recusa a seleção vazia. Desabilitado enquanto
                                 o quadro não carregou uma vez: antes disso não há
                                 `ordenacao_colunas` e o PUT (que exige o registro
                                 completo) não teria o que enviar. -->
                            <MultiSelect
                                v-if="modo === 'kanban'"
                                :modelValue="colunasVisiveis"
                                :options="colunasOpcoes"
                                optionLabel="descricao"
                                optionValue="id"
                                :showToggleAll="false"
                                :maxSelectedLabels="0"
                                :selectedItemsLabel="`{0} de ${colunasOpcoes.length} colunas`"
                                :disabled="!preferencia.ordenacao_colunas || !colunasOpcoes.length"
                                size="small"
                                class="w-10rem"
                                aria-label="Colunas visíveis no quadro"
                                v-tooltip.top="'Colunas visíveis no quadro'"
                                @update:modelValue="mudarColunas"
                            >
                                <!-- Botão, não interruptor: o `showToggleAll` do
                                     PrimeVue também DESmarca tudo, e desmarcar tudo é
                                     justamente o estado que a guarda recusa — um
                                     controle que parece interruptor e responde com
                                     toast de recusa ensina errado. Este só marca, e
                                     some de vista (desabilitado) quando já não tem o
                                     que marcar. Grava pelo mesmo caminho de qualquer
                                     outra troca de seleção. -->
                                <template #header>
                                    <div class="px-3 pt-2">
                                        <Button
                                            label="Selecionar todas"
                                            icon="pi pi-check-square"
                                            text
                                            size="small"
                                            class="w-full justify-content-start"
                                            :disabled="todasColunasVisiveis"
                                            @click="selecionarTodasColunas"
                                        />
                                    </div>
                                </template>
                            </MultiSelect>
                            <Button icon="pi pi-refresh" label="Atualizar" severity="warn" size="small"
                                @click="atualizar" />
                        </div>
                    </template>
                </Toolbar>

                <KanbanBoard v-if="modo === 'kanban'" ref="kanban" @abrir="openForm" @colunas="onColunasKanban" />

                <template v-else>
                <DataTable :value="lista" :loading="isLoading" size="small" stripedRows showGridlines scrollable
                    scrollHeight="calc(100vh - 300px)" :sortField="params.sort.field" :sortOrder="params.sort.order"
                    @sort="onTableSort">

                    <Column style="width: 100px" field="id" header="#" sortable></Column>
                    <Column field="id_primario" header="ID Primario" sortable></Column>
                    <Column field="id_secundario" header="ID Secundario" sortable></Column>
                    <Column field="cliente" header="Cliente" sortable></Column>
                    <Column field="site" header="Site/Localidade" sortable></Column>
                    <Column field="cidade" header="Cidade" sortable>
                        <template #body="{ data }">
                            {{ data.cidade }}
                            <template v-if="data.estado_id">
                                ({{data.uf}})
                            </template>
                        </template>
                    </Column>
                    <Column field="responsavel" header="Responsável" sortable></Column>
                    <Column field="etapa" header="Etapa" sortable>
                        <template #body="{ data }">
                            {{ data.etapa_descricao || data.etapa }}
                        </template>
                    </Column>

                    <Column field="data_vencimento" header="Vencimento" sortable style="width: 130px">
                        <template #body="{ data }">
                            <template v-if="data.data_vencimento">
                                <Badge
                                    :value="vencimentoLabel(data.data_vencimento)"
                                    :severity="vencimentoSeverity(data.data_vencimento)"
                                    v-tooltip.top="'Vence em: ' + formatDate(data.data_vencimento)"
                                />
                            </template>
                        </template>
                    </Column>

                    <Column style="width: 150px" bodyClass="flex justify-content-end gap-1">
                        <template #body="{ data }">
                            <Badge v-if="data.pendencias" :value="data.pendencias" severity="warn"></Badge>
                            <Badge
                                v-if="data.classificacao_conclusao"
                                value="C"
                                :severity="data.evidencias > 0 ? 'success' : 'warn'"
                                v-tooltip.top="data.classificacao_conclusao"
                            ></Badge>
                            <Button
                                v-if="data.cancelamento_id"
                                icon="pi pi-ban"
                                title="Cancelamento em andamento"
                                aria-label="Cancelamento em andamento"
                                v-tooltip.top="'Cancelamento em andamento'"
                                class="p-button-rounded p-button-text p-button-warning p-0 w-2rem h-2rem"
                                @click="irParaCancelamentoId(data.cancelamento_id)"
                            />
                            <Button icon="pi pi-pen-to-square" title="Atualizar" class="p-button-rounded p-button-text p-button-info p-0 w-2rem h-2rem"
                                @click="openForm(data)" />
                            <Button
                                icon="pi pi-trash"
                                title="Remover ou cancelamento"
                                class="p-button-rounded p-button-text p-button-danger p-0 w-2rem h-2rem"
                                aria-haspopup="true"
                                :aria-controls="acaoMenuId"
                                @click="toggleAcaoContrato($event, data)"
                            />
                        </template>
                    </Column>
                    <template #empty>
                        <div class="text-center text-gray-600">
                            Nenhum registro encontrado
                        </div>
                    </template>
                </DataTable>

                <div class="flex justify-content-between flex-wrap">
                    <div class="flex align-items-center justify-content-center">
                        <div class="flex justify-end mt-2 text-sm text-gray-600">
                            <span>{{ pagination.total }} registros</span>
                        </div>

                    </div>
                    <div class="flex align-items-center justify-content-center">
                        <Paginator :first="pagination.first" :rows="pagination.perPage" :totalRecords="pagination.total"
                            @page="onPage">
                        </Paginator>
                    </div>
                    <div class="flex align-items-center justify-content-center">
                    </div>
                </div>
                </template>

            </template>
        </Card>
        <ContratoForm :visible="visibleForm" :id="item.id || 0" :data="item" :initial-tab="initialTabForm" :permissoes="permissoes" @close="closeForm" @save="atualizar" />
        <UploadForm :visible="visibleUploadForm" :permissoes="permissoes" @close="closeForm" @save="atualizar" />

        <Menu :id="acaoMenuId" ref="acaoMenu" :model="acaoMenuItems" popup />

        <MarcarCancelamentoDialog
            v-model:visible="visibleMarcarCancelamento"
            :contrato-id="marcarCancelamentoContratoId"
            @saved="onCancelamentoMarcado"
            @show-cancelamento="irParaCancelamento"
        />

        <Popover ref="busca" :closeOnEscape="false" :dismissable="false" class="w-3">
            <div class="w-full">
                <form @submit.prevent="onSubmitBusca">
                    <div class="w-full">
                        <div class="field mt-4">
                            <FloatLabel>
                                <InputText v-model="params.filter['contrato']" class="w-full" />
                                <label>ID Primário e Secundário</label>
                            </FloatLabel>
                        </div>
                        <div class="field mt-4">
                            <FloatLabel>
                                <MultiSelect v-model="params.filter['con.solicitante_id']" display="chip" :maxSelectedLabels="6"
                                    :options="solicitanteOpt" optionLabel="descricao" filter
                                    class="w-full" optionValue="id" />
                                <label>Solicitante</label>
                            </FloatLabel>
                        </div>
                        <div class="field mt-4">
                            <FloatLabel>
                                <InputText v-model="params.filter['cli.nome']" class="w-full" />
                                <label>Cliente</label>
                            </FloatLabel>
                        </div>
                        <div class="field mt-4">
                            <FloatLabel>
                                <InputText v-model="params.filter['site']" class="w-full" />
                                <label>Site/Localidade</label>
                            </FloatLabel>
                        </div>
                        <div class="field mt-4">
                            <FloatLabel>
                                <Select v-model="params.filter['con.estado_id']" :options="estadoOpt"
                                    optionLabel="nome" filter @change="onEstadoChange"
                                    class="w-full" optionValue="id" />
                                <label>Estado</label>
                            </FloatLabel>
                        </div>
                        <div class="field mt-4">
                            <FloatLabel>
                                <MultiSelect v-model="params.filter['con.cidade_id']" display="chip" :maxSelectedLabels="6"
                                    :options="cidadeOpt" optionLabel="nome" filter
                                    class="w-full" optionValue="id" />
                                <label>Cidade</label>
                            </FloatLabel>
                        </div>
                        <div class="field mt-4">
                            <FloatLabel>
                                <MultiSelect v-model="params.filter['con.etapa']" display="chip" :maxSelectedLabels="6"
                                    :options="etapaOpt" optionLabel="descricao" filter
                                    class="w-full" optionValue="id" />
                                <label>Etapa</label>
                            </FloatLabel>
                        </div>
                        <div class="field mt-4">
                            <FloatLabel>
                                <Select v-model="params.filter['classificacao_conclusao']" :options="classificacaoConclusaoOpt"
                                    optionLabel="descricao" filter showClear
                                    class="w-full" optionValue="id" />
                                <label>Classificação da Conclusão</label>
                            </FloatLabel>
                        </div>
                        <div class="field mt-4">
                            <FloatLabel>
                                <MultiSelect v-model="params.filter['responsavel_id']" display="chip" :maxSelectedLabels="6"
                                    :options="responsavelOpt" optionLabel="nome" filter
                                    class="w-full" optionValue="id" />
                                <label>Responsável</label>
                            </FloatLabel>
                        </div>
                        <div class="field mt-4">
                            <FloatLabel>
                                <Select v-model="params.filter['fechamento']" :options="[{id: 1, 'descricao': 'Aguardando Fechamento'}]"
                                    optionLabel="descricao" filter showClear
                                    class="w-full" optionValue="id" />
                                <label>Fechamento</label>
                            </FloatLabel>
                        </div>
                    </div>

                    <div class="flex justify-content-end gap-1">
                        <Button type="submit" label="Buscar" severity="success" icon="pi pi-search"
                            class="flex align-items-center justify-content-center" />
                        <Button icon="pi pi-eraser" label="Limpar" severity="warn" size="small"
                            class="flex align-items-center justify-content-center" @click="onClearBusca" />
                        <Button icon="pi pi-times" label="Fechar" severity="danger" size="small"
                            class="flex align-items-center justify-content-center" @click="toggleBusca" />
                    </div>
                </form>
            </div>
        </Popover>
    </div>
</template>
<script>
import Card from 'primevue/card';
import Badge from 'primevue/badge';

/* Table */
import Toolbar from 'primevue/toolbar';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import DataTable from 'primevue/datatable';
import Paginator from 'primevue/paginator';
import Column from 'primevue/column';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';
import SelectButton from 'primevue/selectbutton';

import Popover from 'primevue/popover';
import FloatLabel from 'primevue/floatlabel';
import InputText from 'primevue/inputtext';

import ContratoForm from '@/page/contrato/Form.vue';
import UploadForm from '@/page/contrato/UploadForm.vue';
import KanbanBoard from '@/page/contrato/kanban/KanbanBoard.vue';
import MarcarCancelamentoDialog from '@/page/cancelamento/components/MarcarCancelamentoDialog.vue';

import Contrato from '@/service/contrato.js';
import Estado from '@/service/estado.js';
import Cidade from '@/service/cidade.js';
import Solicitante from '@/service/solicitante.js';
import Etapa from '@/service/etapa.js';
import Usuario from '@/service/usuario.js';
import Kanban from '@/service/contratoKanban.js';

import File from '@/utils/File.js';
import Filter from '@/utils/Filter.js';
import Vencimento from '@/utils/Vencimento.js';

import Moment from 'moment';

export default {
    components: {
        Card,
        Toolbar,
        Button,
        Menu,
        DataTable,
        Paginator,
        Column,
        MultiSelect,
        Select,
        SelectButton,
        Badge,
        KanbanBoard,

        /* Busca */
        Popover,
        FloatLabel,
        InputText,

        /* Form */
        ContratoForm,
        UploadForm,
        MarcarCancelamentoDialog,
    },
    data() {
        return {
            acaoMenuId: 'contrato-lista-acao-menu',
            acaoMenuItems: [],
            acaoContrato: null,
            isLoading: false,
            lista: [],
            listaCarregada: false,
            item: {},

            modo: 'lista',
            modoOpcoes: [
                { label: 'Lista', value: 'lista', icon: 'pi pi-list' },
                { label: 'Kanban', value: 'kanban', icon: 'pi pi-th-large' },
            ],
            preferencia: { colunas_visiveis: null, ordenacao_colunas: null },
            // Teto de permissão, vindo do quadro (`colunas_permitidas`). Antes o
            // seletor lia o catálogo global do blade, que não sabe de permissão:
            // perfil com uma etapa via "7 de 7 colunas" ao lado de um quadro de
            // uma coluna só.
            colunasPermitidas: [],
            preferenciaSalva: null,
            visibleForm: false,
            visibleUploadForm: false,
            visibleMarcarCancelamento: false,
            marcarCancelamentoContratoId: null,
            initialTabForm: null,

            params: {
                filter: {},
                sort: {
                    field: 'id',
                    order: -1
                }
            },
            pagination: {
                page: 0,
                perPage: 0,
                total: 0,
            },
            permissoes: permissoes,

            solicitanteOpt: [],
            isLoadingSolicitanteOpt: false,

            etapaOpt: [],
            isLoadingEtapaOpt: false,
            classificacaoConclusaoOpt: [
                { id: 'com_evidencias', descricao: 'Concluído com evidências' },
                { id: 'sem_evidencias', descricao: 'Concluído sem evidências' },
            ],

            estadoOpt: [],
            isLoadingEstadoOpt: false,

            cidadeOpt: [],
            isLoadingCidadeOpt: false,

            responsavelOpt: [],
            isLoadingResponavelOpt: false,
        }
    },
    beforeMount() {
        document.addEventListener("onPermissios", event => this.permissoes = event.permissoes);
    },
    mounted() {
        this.loadEstadoOpt();
        this.loadSolicitanteOpt();
        this.loadEtapaOpt();
        this.loadUsuarioOpt();

        this.carregarPreferencia();
        this.handleQueryAutoOpen();
    },
    computed: {
        /**
         * As sete colunas do quadro, na ordem do catálogo — inclusive as que o
         * usuário escondeu, que por isso não vêm na resposta do Kanban.
         *
         * `ETAPA_OPTIONS` é o catálogo inteiro (nove); as colunas do quadro são
         * as etapas de topo menos `cancelado`, que é a regra de
         * `EtapaCatalog::colunasKanban()`. Se essa regra mudar no PHP, muda aqui
         * junto — o backend não expõe a lista de colunas separada do quadro.
         */
        colunasOpcoes() {
            return this.colunasPermitidas;
        },
        todasColunasVisiveis() {
            return this.colunasOpcoes.length > 0
                && this.colunasVisiveis.length === this.colunasOpcoes.length;
        },
        /**
         * Marcadas = preferência ∩ permitidas, com os dois fallbacks do serviço:
         * preferência vazia, ou preferência que não sobrou nada depois do corte
         * de permissão, valem "todas as permitidas" — que é o que o quadro ao
         * lado monta nesses casos. A preferência em si não é reescrita pela
         * permissão: quem recuperar a etapa recupera a coluna.
         */
        colunasVisiveis() {
            const permitidas = this.colunasOpcoes.map(({ id }) => id);
            const salvas = this.preferencia.colunas_visiveis;
            const marcadas = salvas?.length
                ? permitidas.filter(id => salvas.includes(id))
                : permitidas;

            return marcadas.length ? marcadas : permitidas;
        },
    },
    methods: {
        /**
         * Preferência de visualização (T5.3/T5.6): um registro só, com modo,
         * colunas visíveis e ordenação por coluna. A lista só é buscada depois
         * de saber o modo — quem entra no Kanban não paga a query da lista.
         */
        carregarPreferencia() {
            Kanban.preferencia()
                .then(({ data }) => {
                    this.preferencia = data.data;
                    this.preferenciaSalva = JSON.stringify(data.data);
                    this.modo = data.data.modo_visualizacao === 'kanban' ? 'kanban' : 'lista';
                })
                // Preferência é conforto, não pré-requisito: falhou, entra na lista.
                .catch(() => {})
                .finally(() => {
                    if (this.modo === 'lista') this.loadData();
                });
        },
        mudarModo(valor) {
            if (!valor || valor === this.modo) return;

            this.modo = valor;
            if (valor === 'lista' && !this.listaCarregada) this.loadData();
            this.salvarPreferencia();
        },
        /**
         * O PUT exige o registro completo — colunas visíveis e ordenação das
         * sete colunas. Enquanto o Kanban não carregou uma vez nesta conta não
         * há como montá-lo, e nesse estado o modo salvo continua sendo o que o
         * servidor já tinha (que só pode ser `lista`).
         */
        salvarPreferencia() {
            const { colunas_visiveis, ordenacao_colunas } = this.preferencia;
            if (!colunas_visiveis?.length || !ordenacao_colunas) return Promise.resolve(false);

            const payload = {
                modo_visualizacao: this.modo,
                colunas_visiveis,
                ordenacao_colunas,
            };

            const assinatura = JSON.stringify(payload);
            if (assinatura === this.preferenciaSalva) return Promise.resolve(true);
            this.preferenciaSalva = assinatura;

            // Resolve em booleano em vez de propagar a falha: quem só salva de
            // passagem (modo, ordenação) ignora o retorno, e quem depende do
            // servidor ter aceitado — o seletor de colunas — sabe desfazer.
            return Kanban.salvarPreferencia(payload)
                .then(() => true)
                .catch(erro => {
                    this.preferenciaSalva = null;
                    this.$toast.add({
                        severity: 'warn',
                        summary: 'Preferência não salva',
                        detail: erro.response?.data?.msg || erro.message,
                        life: 4000,
                    });
                    return false;
                });
        },
        /**
         * Esconder tudo não é um estado que a tela saiba explicar (o backend cai
         * no quadro inteiro quando a lista vem vazia, e o PUT recusa com `min:1`),
         * então a última coluna não desmarca: avisa e devolve a seleção anterior.
         * Como `modelValue` é prop, a devolução precisa de um array novo — o
         * MultiSelect guarda a seleção internamente e só a ressincroniza quando a
         * prop muda de identidade.
         */
        // Passa por `mudarColunas` de propósito: um só caminho de gravação
        // (PUT, e o quadro só recarrega se voltou 200).
        selecionarTodasColunas() {
            if (this.todasColunasVisiveis) return;
            this.mudarColunas(this.colunasOpcoes.map(({ id }) => id));
        },
        mudarColunas(valor) {
            if (!valor.length) {
                this.preferencia.colunas_visiveis = [...this.colunasVisiveis];
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Colunas do quadro',
                    detail: 'Deixe ao menos uma coluna visível.',
                    life: 3000,
                });
                return;
            }

            const anterior = [...this.colunasVisiveis];
            const permitidas = this.colunasOpcoes.map(({ id }) => id);
            // O que a permissão esconde continua gravado: o seletor só manda no
            // que ele mostra. Sem isto, o primeiro clique de quem perdeu acesso a
            // uma etapa apagaria a preferência dela, e recuperar a permissão não
            // recuperaria a coluna.
            const foraDaPermissao = (this.preferencia.colunas_visiveis || [])
                .filter(id => !permitidas.includes(id));
            // Ordem do catálogo, não a ordem em que o usuário marcou: é a ordem
            // em que o quadro monta as colunas.
            this.preferencia.colunas_visiveis = [
                ...permitidas.filter(id => valor.includes(id)),
                ...foraDaPermissao,
            ];

            this.salvarPreferencia().then(salvou => {
                // O quadro é montado a partir da preferência no servidor. Se ela
                // não foi salva, recarregar mostraria colunas que não batem com
                // o que o seletor exibe.
                if (salvou) this.$refs.kanban?.carregar();
                else this.preferencia.colunas_visiveis = anterior;
            });
        },
        onColunasKanban({ colunas, permitidas }) {
            this.colunasPermitidas = permitidas;
            this.preferencia.ordenacao_colunas = {
                ...(this.preferencia.ordenacao_colunas || {}),
                ...Object.fromEntries(colunas.map(({ id, ordenacao }) => [id, ordenacao])),
            };
            this.preferencia.colunas_visiveis = this.preferencia.colunas_visiveis?.length
                ? this.preferencia.colunas_visiveis
                : colunas.map(({ id }) => id);

            this.salvarPreferencia();
        },
        atualizar() {
            if (this.modo === 'kanban') {
                this.$refs.kanban?.carregar();
                return;
            }
            this.loadData();
        },
        handleQueryAutoOpen() {
            const query = this.$route.query || {};
            const openForm = String(query.open_form || '') === '1';
            const contratoId = query.contrato_id;
            if (!openForm || !contratoId) return;

            const tabMap = {
                'assinatura': constantes.ETAPA.ASSINATURA,
                'cotacao': constantes.ETAPA.COTACAO,
                'provedor': constantes.ETAPA.PROVEDOR,
                'agendamento': constantes.ETAPA.AGENDAMENTO,
                'instalacao': constantes.ETAPA.INSTALACAO,
                'pendencias': 'pendencias',
            };
            this.initialTabForm = tabMap[query.tab] ?? constantes.ETAPA.LEAD;

            this.isLoading = true;
            Contrato.get(contratoId)
                .then(response => {
                    const contrato = response.data?.data || response.data;
                    if (!contrato || !contrato.id) return;
                    this.openForm(contrato);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                })
                .catch(error => {
                    const msg = error.response?.data?.msg || error.message;
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: msg, life: 5000 });
                })
                .finally(() => {
                    this.isLoading = false;
                    this.$router.replace({ name: this.$route.name, query: {} });
                });
        },
        dateFormat(value) {
            if (!value) return;
            return Moment.utc(value).local().format("DD/MM/YYYY HH:mm");
        },
        // Mesma regra do card do Kanban, uma implementação só.
        formatDate(value) {
            return Vencimento.data(value);
        },
        vencimentoLabel(value) {
            return Vencimento.rotulo(value);
        },
        vencimentoSeverity(value) {
            return Vencimento.severidade(value);
        },
        openForm(item) {
            this.item = { ...item };
            this.visibleForm = true;
        },
        closeForm(event) {
            this.item = {};
            this.visibleForm = false;
            this.visibleUploadForm = false;
            this.initialTabForm = null;
        },
        openUploadForm() {
            this.visibleUploadForm = true;
        },
        toggleAcaoContrato(event, data) {
            this.acaoContrato = data;
            const itens = [];

            if (this.permissoes.CANCELAMENTO_INCLUIR) {
                itens.push({
                    label: 'Marcar cancelamento',
                    icon: 'pi pi-times-circle',
                    command: () => this.abrirMarcarCancelamento(this.acaoContrato),
                });
            }

            if (this.permissoes.CONTRATO_REMOVER) {
                itens.push({
                    label: 'Remover',
                    icon: 'pi pi-trash',
                    command: () => this.remove(this.acaoContrato),
                });
            }

            if (!itens.length) {
                return;
            }

            this.acaoMenuItems = itens;
            this.$refs.acaoMenu.toggle(event);
        },
        abrirMarcarCancelamento(data) {
            this.marcarCancelamentoContratoId = data.id;
            this.visibleMarcarCancelamento = true;
        },
        onCancelamentoMarcado() {
            this.visibleMarcarCancelamento = false;
            this.marcarCancelamentoContratoId = null;
            this.loadData();
        },
        irParaCancelamento(cancelamento) {
            this.visibleMarcarCancelamento = false;
            this.$router.push({ name: 'cancelamento.tratativa', params: { id: cancelamento.id } });
        },
        irParaCancelamentoId(cancelamentoId) {
            this.$router.push({ name: 'cancelamento.tratativa', params: { id: cancelamentoId } });
        },
        remove(data) {
            this.$confirm.require({
                message: 'Deseja realmente remover o item?',
                header: 'Remover',
                acceptLabel: 'Remover',
                icon: 'pi pi-trash',
                acceptClass: 'bg-red-500 text-white border-red-500',
                rejectLabel: 'Cancelar',
                rejectClass: 'bg-red-400 text-white',
                accept: () => {
                    this.isLoading = true;
                    Contrato.delete(data.id)
                        .then(response => {
                            this.$toast.add({ severity: 'success', summary: 'Sucesso', detail: 'O item foi removido com sucesso.', life: 3000 });
                            this.loadData();
                        })
                        .catch(error => {
                            if (error.response) {
                                let err = error.response.data;
                                if (err.erros) {
                                    for (let i in err.erros) {
                                        this.errors[i] = err.erros[i][0];
                                    }
                                    return;
                                }
                                if (error.response.data.msg) {
                                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                                    return;
                                }
                            }
                            this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                        })
                        .finally(() => {
                            this.isLoading = false;
                        });
                },
            });
        },
        loadDataParams(usePage = true) {
            let params = [];

            // Pagination
            params.push(`page=${1 + this.pagination.page}`);

            // Filter
            params = Filter.formToQuery(this.params.filter, params);

            // Order
            params.push(`order[${this.params.sort.field}]=` + (this.params.sort.order > 0 ? 'asc' : 'desc'));
            return params;
        },
        exportarData() {
            let params = this.loadDataParams();

            this.isLoading = true;
            Contrato.download(params.join("&"))
                .then(response => {
                    File.downloadBlob(response);
                })
                .catch(error => {
                    if (error.response) {
                        if (error.response.data.msg) {
                            this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                            return;
                        }
                    }
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        loadData() {
            let params = this.loadDataParams();

            this.isLoading = true;
            Contrato.list(params.join("&"))
                .then(response => {
                    this.lista = response.data.data;
                    this.listaCarregada = true;
                    this.pagination.perPage = response.data.per_page;
                    this.pagination.total = response.data.total;
                    this.pagination.first = response.data.per_page * this.pagination.page;
                })
                .catch(error => {
                    if (error.response) {
                        if (error.response.data.msg) {
                            this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                            return;
                        }
                    }
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        // Filter
        toggleBusca(event) {
            this.$refs.busca.toggle(event);
        },
        onClearBusca(event) {
            this.toggleBusca(event);
            this.params.filter = {};
            this.onPage();
        },
        onSubmitBusca(event) {
            this.toggleBusca(event);
            this.onPage();
        },


        // Paginate
        onPage(event) {
            const page = event ? event.page : 0;
            this.pagination.page = page;
            this.$router.replace({ name: this.$route.name, query: { page: page } });
            this.loadData();
        },

        // Sort
        onTableSort(event) {
            this.params.sort = {
                field: event.sortField,
                order: event.sortOrder,
            };
            this.loadData();
        },

        onEstadoChange(event) {
            this.params.filter['con.cidade_id'] = null;
            if(event.value) {
                this.loadCidadeOpt(event.value);
            }
        },
        loadEstadoOpt() {
            this.isLoadingEstadoOpt = true;
            Estado.option(['order[descricao]=asc'].join("&"))
                .then(response => {
                    this.estadoOpt = response.data;
                })
                .finally(() => {
                    this.isLoadingEstadoOpt = false;
                });
        },
        loadCidadeOpt(estadoId) {
            if(!estadoId) {
                this.cidadeOpt = [];
                return;
            }

            this.isLoadingCidadeOpt = true;
            Cidade.option(estadoId, ['order[nome]=asc'].join("&"))
                .then(response => {
                    this.cidadeOpt = response.data;
                })
                .finally(() => {
                    this.isLoadingCidadeOpt = false;
                });
        },
        loadUsuarioOpt() {

            this.isLoadingResponavelOpt = true;
            Usuario.option(['order[nome]=asc'].join("&"))
                .then(response => {
                    let responsaveis    = [{ id: 0, nome: "[SEM RESPONSAVEL]" }];
                    this.responsavelOpt = responsaveis.concat(response.data);
                })
                .finally(() => {
                    this.isLoadingResponavelOpt = false;
                });
        },
        loadEtapaOpt() {
            this.isLoadingEtapaOpt = true;
            Etapa.option(['order[ordem]=asc'].join("&"))
                .then(response => {
                    this.etapaOpt = response.data;
                })
                .finally(() => {
                    this.isLoadingEtapaOpt = false;
                });
        },
        loadSolicitanteOpt() {
            this.isLoadingSolicitanteOpt = true;
            Solicitante.option(['order[descricao]=asc'].join("&"))
                .then(response => {
                    this.solicitanteOpt = response.data;
                })
                .finally(() => {
                    this.isLoadingSolicitanteOpt = false;
                });
        },
    }
}
</script>
